import axios, { AxiosInstance, AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { API_URL } from "../config/env";

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ===============================
// REQUEST INTERCEPTOR
// ===============================

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===============================
// RESPONSE INTERCEPTOR
// ===============================

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      toast.error("Session expired. Please login again.");

      window.location.href = "/auth/login";
    }

    if (status === 403) {
      toast.error(
        "Access denied. You do not have permission to perform this action."
      );
    }

    if (status >= 500) {
      toast.error("Server error. Please try again later.");
    }

    return Promise.reject(error);
  }
);

// ===============================
// TYPES
// ===============================

export interface LoginRequest {
  email: string;
  password: string;
}

export type GrievanceStatus = "pending" | "in-progress" | "resolved" | "rejected";

export type UserRole =
  | "super_admin"
  | "ministry_admin"
  | "office_admin";
  
export interface LoginResponse {
  user: {
    id: number;
    name: string;
    email: string;
    role: UserRole;
    phone?: string | null;
    ministry_name?: string | null;
    ministry_id?: number | null;
    office_id?: number | null;
    createdAt?: string;
  };
  token: string;
}

// ===============================
// AUTH API
// ===============================

export const authAPI = {
  login: (data: LoginRequest) =>
    api.post<LoginResponse>("/auth/login", data),

  register: (data: any) =>
    api.post("/auth/register", data),

  updateProfile: (data: {
    name?: string;
    email?: string;
    phone?: string;
  }) =>
    api.put("/auth/me", data),

  changePassword: (data: any) =>
    api.post("/auth/change-password", data),

  forgotPassword: (data: any) =>
    api.post("/auth/forgot-password", data),

  resetPassword: (data: any) =>
    api.post("/auth/reset-password", data),

  logout: () =>
    api.post("/auth/logout"),
};

// ===============================
// PUBLIC API
// ===============================

export const publicAPI = {
  submitGrievance: (data: any) =>
    api.post("/public/grievances", data),

  trackGrievance: (grievanceId: string) =>
    api.get(`/public/grievances/track/${grievanceId}`),

  getPublicStats: () =>
    api.get("/public/stats"),

  getCategories: () =>
    api.get("/public/categories"),

  getDepartments: () =>
    api.get("/public/departments"),
};

// ===============================
// GRIEVANCE API
// ===============================

export const grievancesAPI = {
  getAll: (params?: any) =>
    api.get("/grievances", { params }),

  getAllSecured: (params?: any) =>
    api.get("/grievances/secured", { params }),

  getById: (id: number) =>
    api.get(`/grievances/${id}`),

  update: (id: number, data: any) =>
    api.put(`/grievances/${id}`, data),

  updateStatus: (
    id: number,
    data: {
      status:
        | "pending"
        | "in-progress"
        | "resolved"
        | "rejected";
    }
  ) =>
    api.patch(`/grievances/${id}/status`, data),

  assign: (id: number, data: any) =>
    api.post(`/grievances/${id}/assign`, data),

  escalate: (id: number, data: any) =>
    api.post(`/grievances/${id}/escalate`, data),

  bulkAction: (data: any) =>
    api.post("/grievances/bulk", data),

  getStats: () =>
    api.get("/grievances/stats/overview"),

  getTimeline: (grievanceId: string) =>
    api.get(`/grievances/${grievanceId}/timeline`),

  addTimeline: (
    grievanceId: string,
    data: {
      status: string;
      message: string;
      office?: string;
      eventDate?: string;
    }
  ) =>
    api.post(
      `/grievances/${grievanceId}/timeline`,
      data
    ),
};

// ===============================
// MINISTRIES API
// ===============================

export const ministriesAPI = {
  getAll: () =>
    api.get("/ministries"),

  getStats: () =>
    api.get("/ministries/stats"),
};

// ===============================
// OFFICES API
// ===============================

export const officesAPI = {
  byMinistry: (ministryId: number) =>
    api.get(`/offices/by-ministry/${ministryId}`),

  create: (data: {
    name: string;
    address?: string;
    ministry_id: number;
  }) =>
    api.post("/offices", data),

  createAdmin: (data: {
    fullName: string;
    email: string;
    password: string;
    phoneNumber?: string;
    ministry_id?: number;
    office_id: number;
  }) =>
    api.post("/offices/admins", data),

  update: (
    id: number,
    data: {
      name?: string;
      address?: string;
    }
  ) =>
    api.patch(`/offices/${id}`, data),

  toggle: (id: number) =>
    api.post(`/offices/${id}/toggle`),

  listAdmins: (ministryId: number) =>
    api.get(
      `/offices/admins/by-ministry/${ministryId}`
    ),
};

// ===============================
// USERS API
// ===============================

export const usersAPI = {
  getAll: () =>
    api.get("/auth/getRegisterAdmin"),

  register: (data: any) =>
    api.post("/auth/register", data),

  update: (id: number, data: any) =>
    api.patch(`/auth/users/${id}`, data),
};

// ===============================
// CATEGORIES API
// ===============================

export const categoriesAPI = {
  getAll: (params?: any) =>
    api.get("/categories", { params }),

  getById: (id: number) =>
    api.get(`/categories/${id}`),

  create: (data: any) =>
    api.post("/categories", data),

  update: (id: number, data: any) =>
    api.put(`/categories/${id}`, data),

  delete: (id: number) =>
    api.delete(`/categories/${id}`),
};

// ===============================
// DEPARTMENTS API
// ===============================

export const departmentsAPI = {
  getAll: (params?: any) =>
    api.get("/departments", { params }),

  getById: (id: number) =>
    api.get(`/departments/${id}`),

  create: (data: any) =>
    api.post("/departments", data),

  update: (id: number, data: any) =>
    api.put(`/departments/${id}`, data),

  delete: (id: number) =>
    api.delete(`/departments/${id}`),

  getStats: (id: number) =>
    api.get(`/departments/${id}/stats`),
};

// ===============================
// ADMIN API
// ===============================

export const adminAPI = {
  getOverviewStats: () =>
    api.get("/admin/stats/overview"),

  getLogs: (params?: any) =>
    api.get("/admin/logs", { params }),

  getAuditLogs: (params?: any) =>
    api.get("/admin/audit", { params }),
};

// ===============================
// REVIEWS API
// ===============================

export const reviewsAPI = {
  submitPublic: (data: {
    grievanceUid: string;
    rating: number;
    comment: string;
    submittedBy?: string;
    submittedEmail?: string;
  }) =>
    api.post("/reviews/public", data),

  getAll: (params?: any) =>
    api.get("/reviews", { params }),

  update: (id: number, data: any) =>
    api.put(`/reviews/${id}`, data),

  updateStatus: (id: number, data: any) =>
    api.patch(`/reviews/${id}/status`, data),

  delete: (id: number) =>
    api.delete(`/reviews/${id}`),
};

// ===============================
// UPLOAD API
// ===============================

export const uploadAPI = {
  uploadFile: (
    file: File,
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();

    formData.append("file", file);

    return api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (
          onProgress &&
          progressEvent.total
        ) {
          const progress = Math.round(
            (progressEvent.loaded * 100) /
              progressEvent.total
          );

          onProgress(progress);
        }
      },
    });
  },
};

export default api;